import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PageSectionHeader from '../../../../../features/components/PageSectionHeader'
import ListItemsTable from '../../../../../features/components/ListItemsTable'
import CampaignGoogleEdit from '../../../../../forms/Google/CampaignForms/CampaignGoogleEdit'
import Can from '../../../../../features/components/Can'
import GoogleAssetGroupCreate from '../../../../../forms/Google/AdForms/GoogleAssetGroup/GoogleAssetGroupCreate'

import usePermissions from '../../../../../hooks/usePermissions'

import { insertIf } from '../../../../../helpers/common'

import { openForm } from '../../../../../modules/actions/forms'
import { updateCampaign } from '../../../../../modules/actions/campaigns'
import {
  campaignErrorSelector,
  campaignLoadingSelector,
  campaignSelector,
  campaignUpdateIsLoadingSelector,
  campaignUpdateSelector,
  campaignWasLoadedSelector
} from '../../../../../modules/selectors/campaigns'
import { selectedGoogleAdAccountSelector } from '../../../../../modules/selectors/adAccounts'

import { GOOGLE_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { CAMPAIGN_GOOGLE_EDIT } from '../../../../../constants/forms'
import { listItemChangeStatus } from '../../../../../constants/other'
import { HISTORY_META_EDIT_SECTION } from '../../../../../constants/changeHistory'

const GoogleCampaignSection = () => {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const campaign = useSelector(campaignSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const campaignUpdate = useSelector(campaignUpdateSelector)
  const campaignUpdateIsLoading = useSelector(campaignUpdateIsLoadingSelector)
  const { id: googleAdAccountId, currency_symbol: currencySymbol } = useSelector(selectedGoogleAdAccountSelector)

  const { id: campaignUpdateId } = campaignUpdate

  const getCampaignActionDropdownOptions = useCallback(
    ({ id, status }) => {
      return [
        ...insertIf(
          permissions.can('update', 'Campaign'),
          {
            text: 'Edit',
            onClickHandler: () => dispatch(openForm({ id, formName: CAMPAIGN_GOOGLE_EDIT }))
          },
          {
            text: status === 'active' ? 'Pause' : 'Activate',
            onClickHandler: () => {
              dispatch(
                updateCampaign(
                  {
                    status: listItemChangeStatus[status].changeTo,
                    account: googleAdAccountId,
                    meta: {
                      [HISTORY_META_EDIT_SECTION]: 'Status'
                    }
                  },
                  id,
                  GOOGLE_PLATFORM
                )
              )
            }
          }
        )
      ]
    },
    [dispatch, googleAdAccountId, permissions]
  )

  const itemsLoadingProps = useMemo(
    () => ({
      initialLoadingSkeletonsNumber: 1
    }),
    []
  )

  return (
    <>
      <PageSectionHeader
        title="Campaign"
        titleIsLoading={!campaignWasLoaded}
        RightPanel={campaignWasLoaded && <GoogleAssetGroupCreate />}
      />
      <ListItemsTable
        items={campaignWasLoaded ? [campaign] : []}
        platform={GOOGLE_PLATFORM}
        currencySymbol={currencySymbol}
        isLoadingSelector={campaignLoadingSelector}
        errorSelector={campaignErrorSelector}
        wasLoadedSelector={campaignWasLoadedSelector}
        getActionsDropdownOptions={getCampaignActionDropdownOptions}
        itemUpdatingId={campaignUpdateIsLoading ? campaignUpdateId : ''}
        showItemPlatformColumn={false}
        itemsLoadingProps={itemsLoadingProps}
      />
      <Can I="update" a="Campaign">
        <CampaignGoogleEdit />
      </Can>
    </>
  )
}

export default GoogleCampaignSection
